/**
 * 视频解码与人脸识别 API
 */
import { FJDecoderManager, FJDecodeMode } from '@fj/codec';
import FJUtil from '../util/FJUtil';
import FJVideoUtil from '../util/FJVideoUtil';
import FJCanvasPool from '../core/FJCanvasPool';
import FJRenderUtil from '../util/FJRenderUtil';
import FJMathUtil from '../util/FJMathUtil';
import FJConfig from '../config/FJConfig';
import FJCoreConfig from '../core/FJCoreConfig';
import { detectFrame } from '../lib/faceDetection/frameDetection';
import {
    detectSubjectBBox,
    disposeSubjectDetector,
    isSubjectDetectAvailable,
    tryEnsureSubjectModelLoaded,
} from '../lib/subjectDetection/subjectDetector';
import { GENERATE_STATE } from '../constants/FJEnumConstant';
import mergeFaceDetectionTimeline, {
    DEFAULT_MERGE_FACE_TIMELINE_OPTIONS,
    advanceMergeTrackStateForRow,
    cloneMergeFrameTrackState,
    createMergeFrameTrackState,
    filterFacesArrayByMinEdge,
} from '../lib/faceDetection/mergeFaceTimeline';

// ─── Constants ───────────────────────────────────────────────────────────────

const DEFAULT_MAX_CONCURRENT_FRAMES = 20;
const DECODE_FPS = 30;

/** @param {number | undefined} fps */
const resolveDecodeFps = fps => (Number.isFinite(fps) && fps > 0 ? fps : DECODE_FPS);
/** 每隔多少解码帧做一次人脸检测；轨道 ID 或数量变化时会对 gap 逐帧细化 */
const FACE_DETECT_FRAME_INTERVAL = 10;
/**
 * 主体检测缓存窗口（帧数）。
 * 同镜头连续无脸时，30帧（≈1s @30fps）内复用上一次主体检测结果，
 * 避免每个采样帧都触发耗时推理（~2300ms/次）。
 */
const SUBJECT_CACHE_WINDOW_FRAMES = 30;

// ─── Module-level canvas & detection cache ────────────────────────────────────

/** 解码缓冲画布（模块级复用，避免每帧重新分配） */
let _decodeBufferCanvas = null;

/**
 * 主体检测结果缓存。
 * seqIndex 用于判断缓存是否仍在有效窗口内。
 * 每次 decodeAndDetect 开始时重置，避免跨视频段复用。
 */
let _subjectCache = { result: null, seqIndex: -Infinity };

// ─── Canvas / frame utilities ─────────────────────────────────────────────────

/** VideoFrame / ImageBitmap 可直接 drawImage，无需额外池化 */
const isDirectDrawableFrameType = type => type === 'VIDEOFRAME' || type === 'IMAGEBITMAP';

/**
 * 将解码 buffer 渲染为已旋转的池化 canvas（调用方负责 release）
 */
const renderDecodeBufferToCanvas = (frame, frameInfo) => {
    const { dst_width, dst_height, rotate, frameType } = frameInfo;
    const imgWidth = rotate === 90 || rotate === 270 ? dst_height : dst_width;
    const imgHeight = rotate === 90 || rotate === 270 ? dst_width : dst_height;

    const canvas = FJCanvasPool.getCanvas();
    canvas.width = dst_width;
    canvas.height = dst_height;

    if (!FJUtil.isCanvasMatchFrameType(_decodeBufferCanvas, frameType)) {
        FJCanvasPool.releaseCanvas(_decodeBufferCanvas);
        _decodeBufferCanvas = FJCanvasPool.getCanvas(FJUtil.getCtxTypeWithFrameType(frameType));
    }

    FJRenderUtil.renderDecodedFrameToCanvas(
        _decodeBufferCanvas,
        frame,
        { width: imgWidth, height: imgHeight },
        frameInfo,
    );

    const ctx = canvas.getContext('2d');
    ctx.save();
    FJMathUtil.changeOrientation(ctx, { width: imgWidth, height: imgHeight }, rotate, 1, 1);
    ctx.drawImage(_decodeBufferCanvas, 0, 0, imgWidth, imgHeight);
    ctx.restore();

    return canvas;
};

const cleanupFrame = frame => {
    if (!frame) return;
    // 解码器通常把 WebCodecs VideoFrame / ImageBitmap 放在 frame.data.data；外层多为无 close 的票据对象
    const inner = frame.data && typeof frame.data === 'object' ? frame.data.data : null;
    const tryClose = resource => {
        if (resource && typeof resource.close === 'function') {
            try {
                resource.close();
            } catch (e) {
                console.error('关闭帧时出错:', e);
            }
        }
    };
    tryClose(inner);
    if (frame !== inner) tryClose(frame);
};

const cleanupAllFrames = pendingFrames => {
    pendingFrames.forEach(cleanupFrame);
    pendingFrames.clear();
};

/**
 * 将 source 缩放到目标检测分辨率，返回池化 raster（调用方负责 release）
 */
const createDetectionRaster = (source, targetWidth, targetHeight) => {
    const raster = FJCanvasPool.getCanvas();
    raster.width = targetWidth;
    raster.height = targetHeight;
    raster.getContext('2d').drawImage(source, 0, 0, targetWidth, targetHeight);
    return raster;
};

// ─── Data utilities ───────────────────────────────────────────────────────────

/** 浅拷贝人脸框数组，供间隔帧沿用上一采样结果 */
const cloneFacesData = facesData => {
    if (!Array.isArray(facesData) || facesData.length === 0) return [];
    return facesData.map(f => ({ ...f }));
};

/**
 * 优先使用帧自带的媒体时间（消除 seqIndex 与真实 PTS 的漂移），否则用 seqIndex 外推。
 *
 * 注意：未带媒体时间时，素材 fps 与 options.fps 不一致、丢帧、VFR 时，rowTime 与真实时刻会有系统性偏差。
 */
const resolveRowTimeSec = (frameData, seqIndex, startTime, endTime, frameStepSec) => {
    const d = frameData && typeof frameData === 'object' ? frameData : null;
    if (d) {
        const raw = d.time ?? d.timestamp ?? d.mediaTime ?? d.pts ?? d.start;
        if (typeof raw === 'number' && Number.isFinite(raw) && raw >= 0) {
            return Math.min(Math.max(startTime, raw), endTime);
        }
    }
    return Math.min(startTime + seqIndex * frameStepSec, endTime);
};

// ─── Callback utilities ───────────────────────────────────────────────────────

const emitProgress = (onProgress, payload) => {
    if (typeof onProgress !== 'function') return;
    try {
        onProgress(payload);
    } catch (e) {
        console.error('onProgress 回调出错:', e);
    }
};

const emitFaceTiming = (onTiming, payload) => {
    if (typeof onTiming !== 'function') return;
    try {
        onTiming(payload);
    } catch (e) {
        console.error('onTiming 回调出错:', e);
    }
};

// ─── Slot release & progress ──────────────────────────────────────────────────

/**
 * 帧处理完成后统一执行：
 * 1. 释放并发槽位
 * 2. 唤醒等待者
 * 3. 上报进度
 */
const releaseFrameSlot = (session, frame, rowTime) => {
    cleanupFrame(frame);
    session.activeFramesCount--;
    session.pendingFrames.delete(frame);
    notifyValidFramesSize(session);
    tryResolveDecodeSlotWaiters(session);
    session.processedFrames += 1;
    emitProgress(session.onProgress, {
        percent: Math.min(100, (session.processedFrames / session.totalFramesEstimate) * 100),
        currentTime: rowTime,
        processedFrames: session.processedFrames,
        totalFramesEstimate: session.totalFramesEstimate,
        phase: GENERATE_STATE.PROCESSING,
    });
};

// ─── Face detection (人脸为空时回退主体检测) ──────────────────────────────────

const buildSubjectDetectOpts = () => ({
    modelUrl: FJConfig.SUBJECT_MODEL_URL,
    device: FJCoreConfig.SUBJECT_DETECT_DEVICE,
    threshold: FJCoreConfig.SUBJECT_DETECT_THRESHOLD,
});

/**
 * 人脸检测，失败时回退主体检测。
 *
 * 【优化】主体检测带帧级缓存：同镜头连续无脸场景，SUBJECT_CACHE_WINDOW_FRAMES 帧内
 * 复用上次推理结果，避免每个采样帧都触发耗时推理（单次 ~2300ms）。
 *
 * @param {HTMLCanvasElement} canvas
 * @param {object} extra  额外传给 detectFrame 的选项
 * @param {number} seqIndex  当前帧的序号，用于缓存有效性判断
 */
const runFaceDetect = async (canvas, extra = {}, seqIndex = 0) => {
    const faces = await detectFrame(canvas, {
        minConfidence: FJCoreConfig.FACE_DETECT_MIN_CONFIDENCE,
        ...extra,
    });
    if (faces.length > 0 || !FJCoreConfig.SUBJECT_DETECT_ENABLED) return faces;

    if (!isSubjectDetectAvailable()) return faces;

    // 缓存命中：同镜头连续无脸，主体几乎不变，直接复用
    if (_subjectCache.result && seqIndex - _subjectCache.seqIndex < SUBJECT_CACHE_WINDOW_FRAMES) {
        return cloneFacesData(_subjectCache.result);
    }

    // 缓存未命中：执行完整主体推理
    try {
        const subdata = await detectSubjectBBox(canvas, buildSubjectDetectOpts());
        if (subdata.length > 0) {
            _subjectCache = { result: subdata, seqIndex };
            return subdata;
        }
        return faces;
    } catch {
        return faces;
    }
};

// ─── Decode config builder ────────────────────────────────────────────────────

const buildDecodeConfig = (file, { startTime, endTime, width, height, fps }) => ({
    fileName: file?.name ?? 'video',
    width,
    height,
    start: startTime,
    end: endTime,
    speed: 1,
    fps,
    samplerate: FJUtil.getAudioSampleRate(),
    mode: FJDecodeMode.FFMPEG,
    frameType: FJVideoUtil.getDecodeVideoFrameType(),
    ignoreNoValidDuration: true,
});

// ─── Detection session ────────────────────────────────────────────────────────

/**
 * 创建一次 decodeAndDetect 调用的可变状态容器。
 * 所有在解码/检测过程中需要共享和修改的状态都集中在此对象上，
 * 避免在 Promise 回调中大量使用闭包变量。
 */
const createDetectionSession = ({
    startTime,
    endTime,
    width,
    height,
    fps,
    maxConcurrentFrames,
    mergeTimelineOpts,
    onProgress,
    onTiming,
}) => {
    const durationSec = Math.max(0, endTime - startTime);
    return {
        // 只读配置
        startTime,
        endTime,
        width,
        height,
        fps,
        maxConcurrentFrames,
        mergeTimelineOpts,
        onProgress,
        onTiming,
        durationSec,
        totalFramesEstimate: Math.max(1, Math.ceil(durationSec * fps)),
        frameStepSec: 1 / Math.max(1e-6, fps),

        // 并发控制
        activeFramesCount: 0,
        pendingFrames: new Set(),
        validFramesSizeResolve: null,
        decodeSlotWaiters: [],

        // 处理进度
        processedFrames: 0,
        decodeOrderIndex: 0,
        /** 采样帧串行检测链：保证人脸检测与 gap flush 的顺序 */
        detectChain: Promise.resolve(),
        faceDecodeDetectT0: 0,

        // 检测结果
        results: [],

        // gap 帧缓冲（采样帧之间的非检测帧，等采样结果出来后决定沿用哪个快照）
        /** @type {{ frameCanvas: HTMLCanvasElement, rowTime: number }[]} */
        gapBuffer: [],
        lastSampleTrackQty: null,
        lastSampleTrackKey: null,
        mergeFrameTrackState: createMergeFrameTrackState(),
        /** @type {Array<{ x: number, y: number, width: number, height: number }> | null} */
        lastSampleFacesSnapshot: null,
    };
};

// ─── Concurrency helpers ──────────────────────────────────────────────────────

const notifyValidFramesSize = session => {
    if (!session.validFramesSizeResolve) return;
    session.validFramesSizeResolve(session.maxConcurrentFrames - session.activeFramesCount);
    session.validFramesSizeResolve = null;
};

/** 每释放一个槽位只唤醒一个等待者，避免并发超限 */
const tryResolveDecodeSlotWaiters = session => {
    if (session.decodeSlotWaiters.length > 0 && session.activeFramesCount < session.maxConcurrentFrames) {
        session.decodeSlotWaiters.shift()();
    }
};

const awaitDecodeSlot = session => {
    if (session.activeFramesCount < session.maxConcurrentFrames) return Promise.resolve();
    return new Promise(res => session.decodeSlotWaiters.push(res));
};

const getValidFramesSize = async session => {
    const available = session.maxConcurrentFrames - session.activeFramesCount;
    if (available > 0) {
        notifyValidFramesSize(session);
        return available;
    }
    return new Promise(res => {
        session.validFramesSizeResolve = res;
    });
};

// ─── Gap buffer management ────────────────────────────────────────────────────

/**
 * 直接输出 gap 内各帧，沿用上一采样快照。
 *
 * 【优化 P2】snapshot 只克隆一次，所有 gap 帧共享同一引用。
 * facesData 元素为纯数字对象，下游 mergeFaceDetectionTimeline 只读，无修改风险。
 */
const flushGapBuffer = session => {
    const sharedSnapshot = cloneFacesData(session.lastSampleFacesSnapshot);
    for (const g of session.gapBuffer) {
        session.results.push({ facesData: sharedSnapshot, currentTime: g.rowTime });
        FJCanvasPool.releaseCanvas(g.frameCanvas);
    }
    session.gapBuffer = [];
};

/**
 * 在 gap 内顺序逐帧检测，找到首个与上一采样「人数或 trackIdsKey」不一致的帧下标。
 *
 * gap 长度最大约 FACE_DETECT_FRAME_INTERVAL - 1，额外检测量可接受。
 * @returns {Promise<{ firstDiff: number, memo: Map<number, { faces: object[], qty: number, key: string }> }>}
 */
const findFirstTrackChangeInGap = async (session, buffer, lastTrackQty, lastTrackKey) => {
    const { mergeTimelineOpts } = session;
    /** @type {Map<number, { faces: object[], qty: number, key: string }>} */
    const memo = new Map();
    const seqState = cloneMergeFrameTrackState(session.mergeFrameTrackState);

    for (let j = 0; j < buffer.length; j++) {
        // gap 帧细化检测不计入主体缓存（seqIndex=-1 永远不命中缓存窗口）
        const faces = await runFaceDetect(buffer[j].frameCanvas, {}, -1);
        const qty = filterFacesArrayByMinEdge(
            faces,
            mergeTimelineOpts.minFaceWidth,
            mergeTimelineOpts.minFaceHeight,
        ).length;
        const meta = advanceMergeTrackStateForRow(buffer[j].rowTime, faces, seqState, mergeTimelineOpts);
        memo.set(j, { faces, qty, key: meta.trackIdsKey });

        if (qty !== lastTrackQty || meta.trackIdsKey !== (lastTrackKey ?? '')) {
            return { firstDiff: j, memo };
        }
    }
    return { firstDiff: buffer.length, memo };
};

/**
 * 当前采样与前一采样轨道不一致时，定位首变帧：
 * - 首变帧之前的帧沿用左侧（前一采样）快照
 * - 首变帧及之后的帧沿用右侧（当前采样）结果
 *
 * @returns {object} advanceMergeTrackStateForRow 推进后的 sampleMeta
 */
const flushGapBufferWithRefinement = async (session, nextFacesData, nextRowTime) => {
    const buffer = session.gapBuffer;

    if (buffer.length === 0) {
        return advanceMergeTrackStateForRow(
            nextRowTime,
            nextFacesData,
            session.mergeFrameTrackState,
            session.mergeTimelineOpts,
        );
    }

    const { firstDiff, memo } = await findFirstTrackChangeInGap(
        session,
        buffer,
        session.lastSampleTrackQty,
        session.lastSampleTrackKey,
    );

    for (let j = 0; j < buffer.length; j++) {
        let facesData;
        if (j < firstDiff) {
            facesData = cloneFacesData(session.lastSampleFacesSnapshot);
        } else if (j === firstDiff) {
            facesData = cloneFacesData(memo.get(firstDiff).faces);
        } else {
            facesData = cloneFacesData(nextFacesData);
        }
        session.results.push({ facesData, currentTime: buffer[j].rowTime });
        FJCanvasPool.releaseCanvas(buffer[j].frameCanvas);
    }
    session.gapBuffer = [];

    return advanceMergeTrackStateForRow(
        nextRowTime,
        nextFacesData,
        session.mergeFrameTrackState,
        session.mergeTimelineOpts,
    );
};

// ─── Frame processing pipeline ────────────────────────────────────────────────

/**
 * 处理 gap 帧（非采样帧）：
 * 仅制作 raster 并推入 gapBuffer，**不执行任何检测**，立即释放槽位。
 *
 * 【优化 P0-2】gap 帧独立于 detectChain 并行运行，不阻塞耗时的采样帧检测链。
 * 由于 canvas 操作全部同步，push 顺序与帧到达顺序一致，无竞态。
 */
const processGapFrame = async (session, frame, seqIndex) => {
    let pooledSource = null;
    let rasterCanvas = null;
    let rowTime = session.startTime;

    try {
        if (!frame?.data) return;

        const { data } = frame;
        const { dst_width, dst_height, data: frameData, type: frameType, rotate } = data;

        rowTime = resolveRowTimeSec(data, seqIndex, session.startTime, session.endTime, session.frameStepSec);

        const source = isDirectDrawableFrameType(frameType)
            ? frameData
            : renderDecodeBufferToCanvas(frameData, { dst_width, dst_height, rotate, frameType });

        if (!isDirectDrawableFrameType(frameType)) pooledSource = source;

        rasterCanvas = createDetectionRaster(source, session.width, session.height);

        if (pooledSource) {
            FJCanvasPool.releaseCanvas(pooledSource);
            pooledSource = null;
        }

        session.gapBuffer.push({ frameCanvas: rasterCanvas, rowTime });
        rasterCanvas = null;
    } catch (err) {
        console.error('gap 帧处理出错:', err);
    } finally {
        if (pooledSource) FJCanvasPool.releaseCanvas(pooledSource);
        if (rasterCanvas) FJCanvasPool.releaseCanvas(rasterCanvas);
        releaseFrameSlot(session, frame, rowTime);
    }
};

/**
 * 处理采样帧：制作 raster → 检测人脸/主体 → 刷新 gapBuffer。
 * 始终在 detectChain 内串行执行，保证检测顺序与 gap flush 的正确性。
 */
const processSampleFrameSerial = async (session, frame, seqIndex) => {
    let pooledSource = null;
    let rasterCanvas = null;
    let rowTime = session.startTime;

    try {
        if (!frame?.data) return;

        const { data } = frame;
        const { dst_width, dst_height, data: frameData, type: frameType, rotate } = data;

        rowTime = resolveRowTimeSec(data, seqIndex, session.startTime, session.endTime, session.frameStepSec);

        const source = isDirectDrawableFrameType(frameType)
            ? frameData
            : renderDecodeBufferToCanvas(frameData, { dst_width, dst_height, rotate, frameType });

        if (!isDirectDrawableFrameType(frameType)) pooledSource = source;

        rasterCanvas = createDetectionRaster(source, session.width, session.height);

        if (pooledSource) {
            FJCanvasPool.releaseCanvas(pooledSource);
            pooledSource = null;
        }

        await processSampleFrame(session, rasterCanvas, rowTime, seqIndex);
        rasterCanvas = null;
    } catch (err) {
        console.error('采样帧处理出错:', err);
    } finally {
        if (pooledSource) FJCanvasPool.releaseCanvas(pooledSource);
        if (rasterCanvas) FJCanvasPool.releaseCanvas(rasterCanvas);
        releaseFrameSlot(session, frame, rowTime);
    }
};

/**
 * 处理采样帧：检测人脸，判断是否需要对前段 gap 做细化，更新轨道状态快照。
 *
 * @param {number} seqIndex 当前帧序号，传入 runFaceDetect 供主体检测缓存使用
 */
const processSampleFrame = async (session, rasterCanvas, rowTime, seqIndex) => {
    const facesData = await runFaceDetect(rasterCanvas, {}, seqIndex);

    // 先在独立克隆上推进状态，避免 needRefineGap 分支中重复推进
    const stateAfterSample = cloneMergeFrameTrackState(session.mergeFrameTrackState);
    let sampleMeta = advanceMergeTrackStateForRow(rowTime, facesData, stateAfterSample, session.mergeTimelineOpts);
    const { trackIdsKey: nextTrackKey, faceCount: nextTrackQty } = sampleMeta;

    const needRefineGap =
        session.lastSampleTrackQty !== null &&
        session.gapBuffer.length > 0 &&
        (session.lastSampleTrackKey !== nextTrackKey || session.lastSampleTrackQty !== nextTrackQty);

    if (needRefineGap) {
        sampleMeta = await flushGapBufferWithRefinement(session, facesData, rowTime);
    } else {
        flushGapBuffer(session);
        session.mergeFrameTrackState = stateAfterSample;
    }

    session.results.push({ facesData, currentTime: rowTime });
    session.lastSampleTrackQty = sampleMeta.faceCount;
    session.lastSampleTrackKey = sampleMeta.trackIdsKey;
    session.lastSampleFacesSnapshot = cloneFacesData(facesData);

    FJCanvasPool.releaseCanvas(rasterCanvas);
};

// ─── Decode callbacks ─────────────────────────────────────────────────────────

const onDecodeEnded = async (session, resolve) => {
    await session.detectChain.catch(() => {});

    // 尾部残留的 gap 帧沿用最后一次采样快照
    flushGapBuffer(session);
    session.results.sort((a, b) => a.currentTime - b.currentTime);

    const decodeDetectMs = performance.now() - session.faceDecodeDetectT0;
    emitProgress(session.onProgress, {
        percent: 100,
        currentTime: session.endTime,
        processedFrames: session.processedFrames,
        totalFramesEstimate: session.totalFramesEstimate,
        phase: 'complete',
    });
    emitFaceTiming(session.onTiming, {
        segmentSec: session.durationSec,
        decodeDetectMs,
        startTime: session.startTime,
        endTime: session.endTime,
    });

    resolve(mergeFaceDetectionTimeline(session.results, session.mergeTimelineOpts));
};

const onDecodeError = (session, reject, error) => {
    cleanupAllFrames(session.pendingFrames);
    if (session.faceDecodeDetectT0 > 0) {
        const elapsed = Math.round((performance.now() - session.faceDecodeDetectT0) / 1000);
        console.warn(
            `[人脸分析·解码检测] 失败 片段时间 ${session.durationSec.toFixed(2)}s | 已耗时 ${elapsed}s`,
            error,
        );
    }
    reject(error);
};

/**
 * 解码帧回调工厂。
 *
 * 【优化 P0-2】采样帧与 gap 帧分流：
 * - 采样帧（每 FACE_DETECT_FRAME_INTERVAL 帧一次）：入 detectChain 串行执行（保证检测顺序）
 * - gap 帧：独立并行，仅做 raster 制作 + buffer 入队，不阻塞检测链
 *
 * 在高耗时主体检测场景（~2300ms/帧），gap 帧可立即释放槽位，
 * 解码器不再因检测链阻塞而长时间挂起。
 */
const makeDecodeCallback = (session, resolve, reject) => async frame => {
    const { data, error, ended } = frame;

    if (ended) {
        await onDecodeEnded(session, resolve);
        return;
    }
    if (error) {
        onDecodeError(session, reject, error);
        return;
    }
    if (!data) return;

    await awaitDecodeSlot(session);
    session.activeFramesCount++;
    session.pendingFrames.add(frame);
    const seqIndex = session.decodeOrderIndex++;
    const isSample = seqIndex % FACE_DETECT_FRAME_INTERVAL === 0;

    if (isSample) {
        // 采样帧：串行入检测链，保证 gap flush 的顺序正确性
        session.detectChain = session.detectChain
            .then(() => processSampleFrameSerial(session, frame, seqIndex))
            .catch(err => console.error('采样帧异步处理失败:', err));
    } else {
        // gap 帧：独立并行运行，仅制作 raster，不执行检测
        processGapFrame(session, frame, seqIndex).catch(err => console.error('gap 帧异步处理失败:', err));
    }
};

// ─── Store ────────────────────────────────────────────────────────────────────

const FJDetectStore = {
    /**
     * 解码视频并进行人脸识别，返回合并后的人脸时间线数据块。
     * @param {File|Blob} file
     * @param {Object} [options]
     * @param {number} options.startTime
     * @param {number} options.endTime
     * @param {number} [options.width]
     * @param {number} [options.height]
     * @param {number} [options.fps] 解码帧率，由上游 `videoMeta.fps` 传入；缺省 30
     * @param {number} [options.maxConcurrentFrames=20]
     * @param {Array}  [options.stableFragments=[]]
     * @param {(p: object) => void} [options.onProgress]
     * @param {(p: { segmentSec: number, decodeDetectMs: number, startTime: number, endTime: number }) => void} [options.onTiming]
     */
    async decodeAndDetect(file, options = {}) {
        const {
            startTime,
            endTime,
            width,
            height,
            maxConcurrentFrames = DEFAULT_MAX_CONCURRENT_FRAMES,
            stableFragments = [],
            fps,
            onProgress,
            onTiming,
        } = options;
        const decodeFps = resolveDecodeFps(fps);

        await FJDetectStore.ensureModelsLoaded();

        // 每次新的解码任务重置主体检测缓存，避免跨视频段复用
        _subjectCache = { result: null, seqIndex: -Infinity };

        _decodeBufferCanvas = FJCanvasPool.getCanvas();

        const mergeTimelineOpts = { ...DEFAULT_MERGE_FACE_TIMELINE_OPTIONS, stableFragments };
        const session = createDetectionSession({
            startTime,
            endTime,
            width,
            height,
            fps: decodeFps,
            maxConcurrentFrames,
            mergeTimelineOpts,
            onProgress,
            onTiming,
        });
        const decodeConfig = buildDecodeConfig(file, { startTime, endTime, width, height, fps: decodeFps });

        emitProgress(onProgress, {
            percent: 0,
            currentTime: startTime,
            processedFrames: 0,
            totalFramesEstimate: session.totalFramesEstimate,
            phase: 'start',
        });

        return new Promise((resolve, reject) => {
            const decodeCallback = makeDecodeCallback(session, resolve, reject);
            try {
                session.faceDecodeDetectT0 = performance.now();
                FJDecoderManager.decodeVideo(file, decodeConfig, {
                    decodeCallback,
                    metadataCallback: () => {},
                    getValidFramesSize: () => getValidFramesSize(session),
                });
            } catch (err) {
                cleanupAllFrames(session.pendingFrames);
                if (session.faceDecodeDetectT0 > 0) {
                    const elapsed = Math.round((performance.now() - session.faceDecodeDetectT0) / 1000);
                    console.warn(
                        `[人脸分析·解码检测] 启动异常 片段时间 ${session.durationSec.toFixed(2)}s | 已耗时 ${elapsed}s`,
                        err,
                    );
                }
                reject(err);
            }
        });
    },

    async ensureModelsLoaded() {
        // 主体检测模型异步预热（不阻塞解码）：首个无脸帧到来前若已就绪可直接命中
        if (FJCoreConfig.SUBJECT_DETECT_ENABLED) {
            tryEnsureSubjectModelLoaded(buildSubjectDetectOpts());
        }
        const faceapi = await import('@vladmandic/face-api');
        if (faceapi.nets.ssdMobilenetv1.params) return;
        await faceapi.nets.ssdMobilenetv1.loadFromUri(FJConfig.FACE_MODEL_URL);
    },

    /** 释放主体检测模型及 buffer（工程切换 / 页面卸载时调用） */
    dispose() {
        disposeSubjectDetector();
    },
};

export default FJDetectStore;
